import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { extractErrorMessage } from '../api/errors';
import type { IntakeConfig } from '../api/intake';
import { claimJoin, joinReadBadge, joinSubmit, type JoinForms } from '../api/join';
import { IntakeWizard } from '../components/intake/IntakeWizard';

const sessionKey = (code: string) => `join-session:${code}`;

function readSession(code: string): string | null {
  try {
    return localStorage.getItem(sessionKey(code));
  } catch {
    return null;
  }
}

function writeSession(code: string, session: string | null): void {
  try {
    if (session) localStorage.setItem(sessionKey(code), session);
    else localStorage.removeItem(sessionKey(code));
  } catch {
    // Private mode: the session lives in memory for this page only.
  }
}

/**
 * Self-enrollment on the participant's own phone (BOOTH-SELF-ENROL). Opened
 * from the QR code on the booth kiosk; public — the single-use code is the
 * only key. Opening it claims the code for this phone (the session token is
 * kept so a reload carries on), then the same intake wizard the booth laptop
 * uses runs in self-service mode.
 */
export function JoinPage() {
  const { code = '' } = useParams();
  const [forms, setForms] = useState<JoinForms | null>(null);
  const [session, setSession] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const stored = readSession(code);
    claimJoin(code, stored)
      .then((f) => {
        if (cancelled) return;
        const s = f.session ?? stored;
        writeSession(code, s);
        setSession(s);
        setForms(f);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(extractErrorMessage(err, 'this link can’t be used'));
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  const config = useMemo<IntakeConfig | null>(
    () =>
      forms && {
        enabled: true,
        consent_family_id: forms.consent_form?.family_id ?? null,
        survey_family_id: forms.survey_form?.family_id ?? null,
        consent_form: forms.consent_form,
        survey_form: forms.survey_form,
      },
    [forms],
  );

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Enrollment
        </Typography>
        <Alert severity="info">{error}</Alert>
      </Container>
    );
  }
  if (!forms || !config || !session) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
        <CircularProgress aria-label="Loading" />
      </Box>
    );
  }
  return (
    <IntakeWizard
      open
      selfService
      title={forms.organisation}
      tenantId=""
      config={config}
      onClose={() => undefined}
      onEnrolled={() => writeSession(code, null)}
      submitFn={(body) => joinSubmit(code, session, body)}
      readBadgeFn={
        forms.badge_reading
          ? (photo) => joinReadBadge(code, session, photo)
          : () => Promise.reject(new Error('type your details in instead'))
      }
    />
  );
}
